'use client';
import React, { useState, useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

interface MFAEnrollModalProps {
  onSuccess: () => void;
  onSkip: () => void;
  isRequired?: boolean;
}

export default function MFAEnrollModal({ onSuccess, onSkip, isRequired = false }: MFAEnrollModalProps) {
  const [step, setStep] = useState<'intro' | 'qr' | 'verify'>('intro');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const supabase = createClient();

  const startEnrollment = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', issuer: 'OncoConsil KSO' });
      if (error) throw error;
      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setFactorId(data.id);
      setStep('qr');
    } catch (err: any) {
      toast.error('Błąd konfiguracji 2FA: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const startChallenge = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.challenge({ factorId });
      if (error) throw error;
      setChallengeId(data.id);
      setStep('verify');
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err: any) {
      toast.error('Błąd inicjowania weryfikacji: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const totpCode = code.join('');
    if (totpCode.length !== 6) { toast.error('Wprowadź 6-cyfrowy kod'); return; }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.mfa.verify({ factorId, challengeId, code: totpCode });
      if (error) throw error;
      toast.success('Uwierzytelnianie dwuetapowe zostało aktywowane');
      onSuccess();
    } catch (err: any) {
      toast.error('Nieprawidłowy kod — sprawdź aplikację i spróbuj ponownie');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnenroll = async () => {
    if (factorId) {
      await supabase.auth.mfa.unenroll({ factorId }).catch(() => {});
    }
    onSkip();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-8 max-h-[90vh] overflow-y-auto">

        {/* STEP: Intro */}
        {step === 'intro' && (
          <>
            <div className="flex flex-col items-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                <Icon name="ShieldCheckIcon" size={32} className="text-primary" />
              </div>
              <h2 className="text-[20px] font-bold text-foreground text-center">Aktywuj uwierzytelnianie 2FA</h2>
              <p className="text-[13px] text-muted-foreground text-center mt-2">
                Platforma OncoConsil przetwarza dane PHI pacjentów onkologicznych. Dwuetapowe uwierzytelnianie jest wymagane zgodnie z wymogami KSO.
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {[
                { icon: 'ShieldIcon', title: 'Ochrona danych PHI', desc: 'Zabezpiecz dostęp do wrażliwych danych medycznych pacjentów' },
                { icon: 'CheckCircleIcon', title: 'Zgodność z KSO', desc: 'Spełnij wymogi bezpieczeństwa Krajowej Sieci Onkologicznej' },
                { icon: 'SmartphoneIcon', title: 'Aplikacja TOTP', desc: 'Użyj Google Authenticator, Authy lub Microsoft Authenticator' },
              ].map(item => (
                <div key={item.icon} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon name={item.icon as 'HomeIcon'} size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-foreground">{item.title}</p>
                    <p className="text-[12px] text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={startEnrollment}
              disabled={isLoading}
              className="w-full py-2.5 rounded-lg bg-primary text-white text-[14px] font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 mb-3"
            >
              {isLoading ? <><Icon name="LoaderIcon" size={16} className="animate-spin" /><span>Konfigurowanie...</span></> : <><Icon name="ShieldCheckIcon" size={16} /><span>Skonfiguruj 2FA teraz</span></>}
            </button>
            {!isRequired && (
              <button onClick={onSkip} className="w-full py-2 text-[13px] text-muted-foreground hover:text-foreground transition-colors">
                Pomiń na razie (niezalecane)
              </button>
            )}
          </>
        )}

        {/* STEP: QR Code */}
        {step === 'qr' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => setStep('intro')} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                <Icon name="ArrowLeftIcon" size={18} className="text-muted-foreground" />
              </button>
              <div>
                <h2 className="text-[18px] font-bold text-foreground">Skanuj kod QR</h2>
                <p className="text-[12px] text-muted-foreground">Krok 1 z 2 — Dodaj konto do aplikacji</p>
              </div>
            </div>

            <p className="text-[13px] text-muted-foreground mb-4">
              Otwórz aplikację uwierzytelniającą (Google Authenticator, Authy) i zeskanuj poniższy kod QR:
            </p>

            {/* QR Code display */}
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-white border-2 border-border rounded-xl shadow-sm">
                {qrCode ? (
                  <img src={qrCode} alt="QR kod do konfiguracji 2FA" width={180} height={180} className="block" />
                ) : (
                  <div className="w-[180px] h-[180px] bg-muted rounded-lg flex items-center justify-center">
                    <Icon name="LoaderIcon" size={24} className="animate-spin text-muted-foreground" />
                  </div>
                )}
              </div>
            </div>

            {/* Manual entry */}
            <div className="bg-muted/40 rounded-lg p-3 mb-5">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Klucz ręczny (jeśli nie możesz zeskanować)</p>
              <div className="flex items-center gap-2">
                <code className="text-[12px] font-mono text-foreground break-all flex-1">{secret}</code>
                <button
                  onClick={() => { navigator.clipboard?.writeText(secret); toast.success('Klucz skopiowany'); }}
                  className="p-1.5 rounded hover:bg-muted transition-colors shrink-0"
                >
                  <Icon name="CopyIcon" size={14} className="text-muted-foreground" />
                </button>
              </div>
            </div>

            <button
              onClick={startChallenge}
              disabled={isLoading}
              className="w-full py-2.5 rounded-lg bg-primary text-white text-[14px] font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 mb-3"
            >
              {isLoading ? <><Icon name="LoaderIcon" size={16} className="animate-spin" /><span>Ładowanie...</span></> : <><span>Dalej — Zweryfikuj kod</span><Icon name="ArrowRightIcon" size={16} /></>}
            </button>
            <button onClick={handleUnenroll} className="w-full py-2 text-[13px] text-muted-foreground hover:text-foreground transition-colors">
              Anuluj konfigurację
            </button>
          </>
        )}

        {/* STEP: Verify */}
        {step === 'verify' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => setStep('qr')} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                <Icon name="ArrowLeftIcon" size={18} className="text-muted-foreground" />
              </button>
              <div>
                <h2 className="text-[18px] font-bold text-foreground">Potwierdź kod</h2>
                <p className="text-[12px] text-muted-foreground">Krok 2 z 2 — Weryfikacja konfiguracji</p>
              </div>
            </div>

            <p className="text-[13px] text-muted-foreground mb-5">
              Wprowadź 6-cyfrowy kod wyświetlany w aplikacji uwierzytelniającej, aby potwierdzić konfigurację:
            </p>

            <div className="flex gap-2 justify-center mb-6" onPaste={handlePaste}>
              {code.map((digit, i) => (
                <input
                  key={`enroll-digit-${i}`}
                  ref={el => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  className={`w-11 text-center text-[20px] font-bold rounded-lg border-2 transition-all focus:outline-none focus:ring-0 ${
                    digit ? 'border-primary bg-primary/5 text-primary' : 'border-input bg-white text-foreground'
                  } focus:border-primary`}
                  style={{ height: '52px' }}
                />
              ))}
            </div>

            <button
              onClick={handleVerify}
              disabled={isLoading || code.join('').length !== 6}
              className="w-full py-2.5 rounded-lg bg-primary text-white text-[14px] font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 mb-3"
            >
              {isLoading ? <><Icon name="LoaderIcon" size={16} className="animate-spin" /><span>Weryfikacja...</span></> : <><Icon name="ShieldCheckIcon" size={16} /><span>Aktywuj 2FA</span></>}
            </button>
            <button onClick={handleUnenroll} className="w-full py-2 text-[13px] text-muted-foreground hover:text-foreground transition-colors">
              Anuluj konfigurację
            </button>
          </>
        )}
      </div>
    </div>
  );
}
